// The signed-out landing: what the site does today, and the ways in (sign in, create an account, browse venues).
// A signed-in visitor never sees it: the route sends them to Home.

import { useTranslation } from '@clavian/i18n/react';
import { Link } from '@tanstack/react-router';
import type { CSSProperties, ReactNode } from 'react';

import { NavIcon, type NavIconName } from '../shell/nav-icons';
import { PublicFrame } from '../shell/PublicFrame';

const FEATURES: readonly { readonly key: 'session' | 'wallet' | 'receipts'; readonly icon: NavIconName }[] = [
  { key: 'session', icon: 'play' },
  { key: 'wallet', icon: 'wallet' },
  { key: 'receipts', icon: 'orders' },
];

export function Landing(): ReactNode {
  const { t } = useTranslation('player');
  return (
    <PublicFrame>
      <div className="pw-landing">
        <section className="pw-hero" aria-labelledby="pw-hero-title">
          <p className="pw-eyebrow">{t('landing.eyebrow')}</p>
          <h1 id="pw-hero-title" className="pw-hero__title">
            {t('landing.title')}
          </h1>
          <p className="pw-hero__sub">{t('landing.subtitle', { brand: 'Clavian' })}</p>
          <div className="pw-hero__cta">
            <Link to="/sign-up" className="pw-btn pw-btn--primary pw-btn--lg">
              {t('landing.ctaSignUp')}
            </Link>
            <Link to="/sign-in" className="pw-btn pw-btn--secondary pw-btn--lg">
              {t('landing.ctaSignIn')}
            </Link>
          </div>
          <p className="pw-hero__browse">
            <Link to="/venues" className="pw-link">
              {t('landing.ctaBrowse')}
            </Link>
          </p>
        </section>
        <ul className="pw-features">
          {FEATURES.map((feature, index) => (
            <li key={feature.key} className="pw-feature" style={{ '--pw-i': index } as CSSProperties}>
              <span className="pw-feature__icon" aria-hidden="true">
                <NavIcon name={feature.icon} size={26} />
              </span>
              <h2 className="pw-feature__title">{t(`landing.features.${feature.key}.title`)}</h2>
              <p className="pw-muted">{t(`landing.features.${feature.key}.body`)}</p>
            </li>
          ))}
        </ul>
        <p className="pw-landing__note pw-subtle">{t('landing.payNote')}</p>
      </div>
    </PublicFrame>
  );
}
